import { useContext, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "aws-amplify";
import { PaginatedTable } from "../../../common/DataDisplay";
import { Spinner } from "flowbite-react";
import Context from "../../../Context/Context";
import InstitutionContext from "../../../Context/InstitutionContext";
import {
  CreditCard,
  CalendarDays,
  User,
  ChevronDown,
  Eye,
  Mail,
  X,
  ExternalLink,
  CheckCircle,
} from "lucide-react";
import { toast } from "react-toastify";

const Billing = () => {
  const navigate = useNavigate();
  const { filteredProductList: productList, userData, isDarkMode = true } = useContext(Context);
  const { institutionData } = useContext(InstitutionContext);
  const [payments, setPayments] = useState([]);
  const [amount, setAmount] = useState("$0");
  const [renewDate, setRenewDate] = useState(userData.renewDate);
  const [loading, setLoading] = useState(false);
  const [filterMonth, setFilterMonth] = useState("");
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [profile, setProfile] = useState(null);
  const [showMore, setShowMore] = useState(false);
  const [currentExpanded, setCurrentExpanded] = useState(false);
  const [expandedPlans, setExpandedPlans] = useState({});
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [currentPlanOverride, setCurrentPlanOverride] = useState(null);

  const PrimaryColor = institutionData?.PrimaryColor || "#000";
  const productMap = useMemo(
    () => (Array.isArray(productList) ? productList : []).reduce((map, p) => ({ ...map, [p.productId]: p }), {}),
    [productList]
  );
  const currentProduct = productMap[userData.productId];

  const normalizeDate = (timestamp) => {
    if (!timestamp) return new Date(0);
    let date = new Date(Number(timestamp));
    if (isNaN(date.getTime()) || date.getFullYear() < 1970)
      date = new Date(Number(timestamp) * 1000);
    return date;
  };

  const handleUpgradeClick = async (cognitoId, productId, order = false) => {
    try {
      if (!productId) {
        toast.error("Product not available");
        return;
      }

      // If user isn't signed in, navigate to signup with productId query so Redirect handles it
      if (!cognitoId) {
        navigate(`/signup?productId=${productId}`);
        return;
      }

      // Build payment URL
      const base =
        process.env.REACT_APP_STAGE === "PROD"
          ? "https://payment.happyprancer.com"
          : "https://betapayment.happyprancer.com";

      let paymentUrl = `${base}/${institutionData?.InstitutionId}/${productId}/${cognitoId}`;
      // append orderType=true if order flag is set
      if (order) {
        paymentUrl += `?orderType=true`;
      }

      // preserve referral if present in localStorage/session (optional)
      const referral =
        sessionStorage.getItem("referral") || localStorage.getItem("referral");
      if (referral) {
        paymentUrl += order ? `&referral=${referral}` : `?referral=${referral}`;
      }

      // optional logging
      console.debug("Redirecting to payment", {
        paymentUrl,
        productId,
        cognitoId,
        order,
      });

      window.location.href = paymentUrl;
    } catch (e) {
      console.error(e);
      toast.error("Unable to start subscription");
    }
  };

  const handleCancelForUpgrade = async () => {
    setIsCancelling(true);
    try {
      // prepare body from userdata/institution
      const body = {
        institution: institutionData?.InstitutionId || institutionData?.institution || "Bworkz",
        cognitoId: userData?.cognitoId || null,
        subscriptionId: userData?.subscriptionId || null,
        paymentId: userData?.paymentId || null,
      };

      await API.patch(
        "user",
        `/user/cancel-subscription-paypal`,
        { body }
      );
      toast.success("Previous subscription cancelled successfully.");
      setShowCancelConfirmModal(false);
      // Proceed to upgrade
      handleUpgradeClick(userData.cognitoId, selectedPlan.productId, false);
    } catch (e) {
      console.error(e);
      toast.error("Could not cancel previous subscription.");
    } finally {
      setIsCancelling(false);
    }
  };

  const handleDirectSwitch = () => {
    handleUpgradeClick(userData.cognitoId, selectedPlan.productId, false);
  };

  const handleSubscriptionCancel = async () => {
    setIsCancelling(true);
    try {
      // prepare body from userdata/institution
      const body = {
        institution: institutionData?.InstitutionId || institutionData?.institution || "Bworkz",
        cognitoId: userData?.cognitoId || null,
        subscriptionId: userData?.subscriptionId || null,
        paymentId: userData?.paymentId || null,
      };

      await API.patch(
        "user",
        `/user/cancel-payment-paypal`,
        { body }
      );
      toast.success("Subscription cancelled successfully.");
      setShowCancelModal(false);
      window.location.reload();
    } catch (e) {
      console.error(e);
      toast.error("Could not cancel subscription.");
    } finally {
      setIsCancelling(false);
    }
  };

  const handleChangeSubscription = () => {
    setShowUpgradeModal(true);
  };

  const handlePlanChange = (plan) => {
    setSelectedPlan(plan);
    const isUpgrade = plan.amount > (currentProduct?.amount || 0);
    if (isUpgrade) {
      setShowCancelConfirmModal(true);
    } else {
      handleDirectSwitch();
    }
  };

  const handleViewInvoice = (payment) => {
    setSelectedPayment(payment);
    setShowInvoiceModal(true);
  };

  const handleViewTransactionOnPayPal = (payment) => {
    if (payment.paymentId) {
      window.open(
        `https://www.paypal.com/cgi-bin/webscr?cmd=_view-a-trans&id=${payment.paymentId}`,
        "_blank"
      );
    } else {
      toast.info("No PayPal transaction ID available for this payment.");
    }
  };

  const toggleCurrentExpanded = () => {
    setCurrentExpanded(!currentExpanded);
  };

  const togglePlanExpanded = (productId) => {
    setExpandedPlans((prev) => ({ ...prev, [productId]: !prev[productId] }));
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await API.get(
          "user",
          `/getReciept/Bworkz/${userData.cognitoId}`,
          {}
        );
        const fetchedPaymentsRaw = (() => {
          if (Array.isArray(res?.payments)) return res.payments;
          if (res?.payments && Array.isArray(res.payments.items)) return res.payments.items;
          if (Array.isArray(res?.items)) return res.items;
          return [];
        })();

        // Normalize amounts to Number and keep the rest
        const fetchedPayments = (fetchedPaymentsRaw || []).map((p) => ({
          ...p,
          amount: Number(p?.amount ?? 0),
        }));

        setPayments(fetchedPayments);
        setProfile(res.profile);

        // Derive a sensible "current" payment: prefer active, then verified & not cancelled, then latest
        if (fetchedPayments.length) {
          const activePayment =
            fetchedPayments.find((p) => p.active) ||
            fetchedPayments.find((p) => p.isVerified && p.status !== "cancelled") ||
            fetchedPayments[0];

          if (activePayment) {
            const symbol = activePayment.currency === "USD" ? "$" : "₹";
            setAmount(`${symbol}${((activePayment.amount || 0) / 100).toFixed(2)}`);
            setRenewDate(activePayment.renewDate || res.profile?.renewDate || userData.renewDate);

            // Provide a lightweight override used in UI when productList/currentProduct is missing
            setCurrentPlanOverride({
              heading: activePayment.subscriptionType || productMap[activePayment.productId]?.heading || "Subscription",
              provides: [],
              amount: activePayment.amount,
              currency: activePayment.currency,
            });
          }
        }
      } catch (e) {
        console.error("Billing data fetch error:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [institutionData.InstitutionId, userData.cognitoId]);

  const orderHistory = useMemo(() => {
    const filteredPayments = filterMonth
      ? (Array.isArray(payments) ? payments : []).filter((p) => {
          const date = normalizeDate(p.paymentDate);
          return (
            p.paymentDate &&
            !isNaN(date.getTime()) &&
            date.toISOString().slice(0, 7) === filterMonth
          );
        })
      : (Array.isArray(payments) ? payments : []);

    const sortedPayments = [...filteredPayments].sort(
      (a, b) =>
        normalizeDate(b.paymentDate).getTime() -
        normalizeDate(a.paymentDate).getTime()
    );

    return sortedPayments.map((p, index) => [
      normalizeDate(p.paymentDate).toLocaleDateString(),
      productMap[p.productId]?.heading || p.subscriptionType || "Subscription",
      p.paymentMode || "N/A",
      `${p.currency === "USD" ? "$" : "₹"}${((Number(p.amount) || 0) / 100).toFixed(2)}`,
      <span
        key={`status-${index}`}
        className={`px-2 py-1 rounded text-xs font-medium ${
          p.isVerified
            ? "bg-green-100 text-green-800 rounded-full px-3"
            : "bg-red-200 text-red-800"
        }`}
      >
        {p.isVerified ? "Paid" : "Halted"}
      </span>,
    ]);
  }, [payments, filterMonth, productMap]);

  const months = useMemo(() => {
    const safePayments = Array.isArray(payments) ? payments : [];
    if (!safePayments.length) return [];
    return [
      ...new Set(
        safePayments
          .map((p) => {
            const date = normalizeDate(p.paymentDate);
            return p.paymentDate && !isNaN(date.getTime())
              ? date.toISOString().slice(0, 7)
              : null;
          })
          .filter(Boolean)
      ),
    ]
      .sort()
      .reverse();
  }, [payments]);

  const institutionName =
    institutionData?.institution ||
    institutionData?.InstitutionId ||
    "Institution";

  const downgrades = useMemo(() => {
    return productList?.filter((plan) => plan.amount < (currentProduct?.amount || 0))
      .sort((a, b) => a.amount - b.amount);
  }, [productList, currentProduct?.amount]);

  const upgrades = useMemo(() => {
    return productList?.filter((plan) => plan.amount > (currentProduct?.amount || 0))
      .sort((a, b) => a.amount - b.amount);
  }, [productList, currentProduct?.amount]);

  // Use displayCurrentProduct as primary source for UI (prefers productList/currentProduct, falls back to override)
  const displayCurrentProduct = currentProduct || currentPlanOverride || {};

  const PlanCard = ({ plan, isExpanded, onToggleExpanded, onChange, currentAmount }) => {
    const planAmount = Number(plan.amount || 0);
    const isUpgrade = planAmount > (currentAmount || 0);
    return (
      <div className="relative bg-white p-4 rounded-lg border mb-4 hover:shadow-sm transition-shadow">
        <button
          onClick={() => onChange(plan)}
          className="absolute top-3 right-3 px-2 py-1 text-xs font-semibold rounded text-white transition-all duration-300 hover:opacity-90"
          style={{ backgroundColor: PrimaryColor }}
        >
          {isUpgrade ? "Upgrade" : "Downgrade"}
        </button>
        <p className="font-bold text-sm mb-2">{plan.heading}</p>
        <div className="space-y-1 mb-2">
          {Array.isArray(plan.provides) &&
            plan.provides
              .slice(0, isExpanded ? plan.provides.length : 2)
              .map((provide, i) => (
                <div key={i} className="flex items-center space-x-1 text-xs text-gray-600">
                  <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                  <span>{provide}</span>
                </div>
              ))}
          {Array.isArray(plan.provides) && plan.provides.length > 2 && (
            <button
              onClick={() => onToggleExpanded(plan.productId)}
              className="text-xs text-primary hover:underline flex items-center space-x-1 w-full justify-start"
              style={{ color: PrimaryColor }}
            >
              <span>{isExpanded ? "View Less" : "View More..."}</span>
              <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`} />
            </button>
          )}
        </div>
        <p className="text-sm font-bold text-green-600">
          {plan.currency === "USD" ? "$" : "₹"}{(planAmount / 100).toFixed(2)}
        </p>
      </div>
    );
  };

  return (
    <main className={`inter no-scrollbar p-8 space-y-8 font-sans relative max-h-[calc(100vh-64px)] overflow-y-auto transition-colors duration-200 ${
      isDarkMode ? 'bg-black text-white' : 'bg-white text-gray-900'
    }`} style={{ fontFamily: "Inter, sans-serif" }}>
      <section className="grid md:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-4">
        <div className={`rounded-xl shadow-sm border-2 border-primaryColor hover:shadow-md transition-all duration-300 p-4 ${
          isDarkMode ? 'bg-[#1a1a1a]' : 'bg-white'
        }`}>
          <div className="flex items-center gap-3">
            <CreditCard style={{ color: PrimaryColor }} className="h-6 w-6" />
            <div className="flex items-center justify-between w-full">
              <p className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Current Plan
              </p>

              <div className="flex space-x-2">
                <button
                  className="px-3 py-1 text-xs font-semibold rounded-md text-white transition-all duration-300 hover:opacity-90"
                  style={{ backgroundColor: PrimaryColor }}
                  onClick={handleChangeSubscription}
                >
                  Change
                </button>
                <button
                  className="px-3 py-1 text-xs font-semibold rounded-md text-red-600 border transition-all duration-300 hover:bg-red-600 hover:text-white"
                  onClick={() => setShowCancelModal(true)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
          <p className="text-base font-bold mt-2">{displayCurrentProduct?.heading}</p>
          {Array.isArray(displayCurrentProduct?.provides) && (
            <div className="mt-2">
              <div
                className={`overflow-hidden transition-all duration-700 ease-in-out ${
                  showMore ? "max-h-96" : "max-h-12"
                }`}
              >
                <div className="space-y-1">
                  {displayCurrentProduct.provides
                    .slice(0, showMore ? displayCurrentProduct.provides.length : 2)
                    .map((provide, i) => (
                      <p key={i} className="text-xs text-gray-600">
                        {provide}
                      </p>
                    ))}
                </div>
              </div>
              {displayCurrentProduct.provides.length > 2 && (
                <button
                  onClick={() => setShowMore(!showMore)}
                  className="text-xs text-gray-700 hover:underline mt-1"
                >
                  {showMore ? "View Less" : "View More..."}
                </button>
              )}
            </div>
          )}
        </div>

        <div className={`rounded-xl shadow-sm border-2 border-primaryColor hover:shadow-md transition-all duration-300 p-4 ${
          isDarkMode ? 'bg-[#1a1a1a]' : 'bg-white'
        }`}>
          <div className="flex items-center gap-3">
            <CalendarDays style={{ color: PrimaryColor }} className="h-6 w-6" />
            <p className="text-sm font-semibold text-gray-600">Next Renewal</p>
          </div>
          <p className="text-base font-bold mt-2">
            {renewDate ? normalizeDate(renewDate).toLocaleDateString() : "N/A"}
          </p>
          <p className="text-sm mt-1 text-gray-500">Auto-renews monthly</p>
        </div>

        <div className={`rounded-xl shadow-sm border-2 border-primaryColor hover:shadow-md transition-all duration-300 p-4 ${
          isDarkMode ? 'bg-[#1a1a1a]' : 'bg-white'
        }`}>
          <div className="flex items-center gap-3">
            <User style={{ color: PrimaryColor }} className="h-6 w-6" />
            <p className="text-sm font-semibold text-gray-600">
              Account Details
            </p>
          </div>
          <div className="mt-4 flex flex-col md:flex-row md:justify-between gap-4">
            <div className="flex-1">
              <p className="text-xs uppercase text-gray-500">Billing Amount</p>
              <p className="text-base font-bold mt-1">{amount}</p>
              <p className="text-sm mt-1 text-gray-500">Billed every cycle</p>
            </div>
            <div className="flex-1 text-right md:text-left">
              <div className="mt-3">
                <p className="text-xs uppercase text-gray-500">Member Since</p>
                <h2 className="font-semibold text-base">
                  {userData.joiningDate
                    ? normalizeDate(userData.joiningDate).toLocaleDateString()
                    : "N/A"}
                </h2>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-semibold">Billing History</h2>
          <div className="relative bg-white rounded-md shadow-sm border border-gray-200 inline-flex items-center min-w-[150px]">
            <CalendarDays className="h-4 w-4 text-gray-500 ml-2" />
            <select
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              className="px-8 py-1.5 text-sm font-medium text-gray-700 bg-transparent border-0 focus:outline-none focus:ring-0 rounded-md cursor-pointer appearance-none"
            >
              <option value="">All Months</option>
              {months.map((month) => (
                <option key={month} value={month}>
                  {new Date(`${month}-01`).toLocaleDateString("en-US", {
                    month: "long",
                    year: "numeric",
                  })}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-gray-600 text-sm py-4">
            <Spinner size="sm" /> <span>Loading history...</span>
          </div>
        ) : (
          <div className={`rounded-xl hover:shadow-md transition-all duration-300 p-2 ${
            isDarkMode ? 'bg-[#1a1a1a]' : 'bg-white'
          }`}>
            <PaginatedTable
              head={[
                "Date",
                "Description",
                "Method",
                "Amount",
                "Status",
              ]}
              data={orderHistory}
              itemsPerPage={4}
            />
          </div>
        )}
      </section>

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full mx-auto">
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-semibold">Cancel Subscription</h3>
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-600 mb-6">
                  Are you sure you want to cancel your subscription? This action
                  cannot be undone.
                </p>
                <div className="flex space-x-3 justify-end">
                  <button
                    onClick={() => setShowCancelModal(false)}
                    disabled={isCancelling}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubscriptionCancel}
                    disabled={isCancelling}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                  >
                    {isCancelling
                      ? "Cancelling..."
                      : "I am sure, cancel subscription"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Cancel Confirmation Modal for Upgrade */}
      {showCancelConfirmModal && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-4">
            <div className="bg-white rounded-lg max-w-md w-full mx-auto">
              <div className="p-4">
                <h3 className="text-lg font-semibold mb-4">Confirm Upgrade</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Your previous subscription will be cancelled. Proceed with upgrading to {selectedPlan?.heading}?
                </p>
                <div className="flex space-x-3 justify-end">
                  <button
                    onClick={() => setShowCancelConfirmModal(false)}
                    disabled={isCancelling}
                    className="px-4 py-2 text-sm font-medium border-2 border-gray-300 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCancelForUpgrade}
                    disabled={isCancelling}
                    className="px-4 py-2 text-sm font-medium text-gray-100 bg-primaryColor rounded-md hover:bg-white hover:text-primaryColor border-2 border-primaryColor disabled:opacity-50 transition duration-500"
                  >
                    {isCancelling
                      ? "Processing..."
                      : "Confirm and Upgrade"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl max-w-6xl w-full mx-auto max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold">Change Plan</h3>
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-4">
                <div className="mb-6 bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-sm mb-2">Current Plan</h4>
                  <p className="text-base font-bold mb-2">{currentProduct?.heading}</p>
                  <div className="space-y-1 mb-2">
                    {Array.isArray(currentProduct?.provides) &&
                      currentProduct.provides
                        .slice(0, currentExpanded ? currentProduct.provides?.length : 2)
                        .map((provide, i) => (
                          <div key={i} className="flex items-center space-x-1 text-xs text-gray-600">
                            <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                            <span>{provide}</span>
                          </div>
                        ))}
                    {Array.isArray(currentProduct?.provides) && currentProduct.provides?.length > 2 && (
                      <button
                        onClick={toggleCurrentExpanded}
                        className="text-xs text-primary hover:underline flex items-center space-x-1 w-full justify-start"
                        style={{ color: PrimaryColor }}
                      >
                        <span>{currentExpanded ? "View Less" : "View More..."}</span>
                        <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${currentExpanded ? "rotate-180" : ""}`} />
                      </button>
                    )}
                  </div>
                  <p className="text-sm font-bold">{amount}</p>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-sm mb-4">Downgrade Options</h4>
                    {downgrades?.length > 0 ? (
                      downgrades.map((plan) => {
                        const isExpanded = expandedPlans[plan.productId] || false;
                        return (
                          <PlanCard
                            key={plan.productId}
                            plan={plan}
                            isExpanded={isExpanded}
                            onToggleExpanded={togglePlanExpanded}
                            onChange={handlePlanChange}
                            currentAmount={currentProduct?.amount || 0}
                          />
                        );
                      })
                    ) : (
                      <p className="text-gray-500 text-sm">No downgrade options available.</p>
                    )}
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm mb-4">Upgrade Options</h4>
                    {upgrades?.length > 0 ? (
                      upgrades.map((plan) => {
                        const isExpanded = expandedPlans[plan.productId] || false;
                        return (
                          <PlanCard
                            key={plan.productId}
                            plan={plan}
                            isExpanded={isExpanded}
                            onToggleExpanded={togglePlanExpanded}
                            onChange={handlePlanChange}
                            currentAmount={currentProduct?.amount || 0}
                          />
                        );
                      })
                    ) : (
                      <p className="text-gray-500 text-sm">No upgrade options available.</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Invoice Modal */}
      {showInvoiceModal && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="p-4 bg-white relative rounded-md max-w-2xl w-full mx-auto">
              <button
                onClick={() => setShowInvoiceModal(false)}
                className="text-gray-400 hover:text-gray-600 absolute top-4 right-4"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="p-2">
                {selectedPayment && profile && (
                  <div className="space-y-2 bg-white print:bg-white">
                    <div className="flex justify-between items-start border-b pb-2 mb-2">
                      <div>
                        <h1
                          className="text-xl font-bold mb-1"
                          style={{ color: PrimaryColor }}
                        >
                          Invoice
                        </h1>
                        <p className="text-xs text-gray-600">
                          {institutionName}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold mb-1">
                          #{selectedPayment.paymentId}
                        </p>
                        <p className="text-xs text-gray-600">
                          Issued:{" "}
                          {normalizeDate(
                            selectedPayment.paymentDate
                          ).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-gray-600">
                          Due:{" "}
                          {normalizeDate(
                            selectedPayment.paymentDate
                          ).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <h3 className="font-bold text-sm mb-1 border-b pb-1">
                          Bill To
                        </h3>
                        <div className="space-y-1 text-xs">
                          <p className="font-semibold">{profile.userName}</p>
                          <p>{profile.emailId}</p>
                          <p>{profile.phoneNumber}</p>
                          {profile.address && <p>{profile.address}</p>}
                          <p>{profile.country || "N/A"}</p>
                        </div>
                      </div>
                      <div>
                        <h3 className="font-bold text-sm mb-1 border-b pb-1">
                          From
                        </h3>
                        <div className="space-y-1 text-xs">
                          <p className="font-semibold">{institutionName}</p>
                          <p className="text-gray-600">Billing Department</p>
                        </div>
                      </div>
                    </div>

                    <div className="border rounded overflow-hidden mb-4">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left p-2 font-semibold text-gray-700 border-b">
                              Description
                            </th>
                            <th className="text-right p-2 font-semibold text-gray-700 border-b">
                              Amount
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b">
                            <td className="p-2">
                              <div className="space-y-1">
                                <p className="font-semibold text-gray-900 text-xs">
                                  {selectedPayment.subscriptionType ||
                                    "Subscription Fee"}
                                </p>
                                <p className="text-xs text-gray-600">
                                  Product ID: {selectedPayment.productId} | Plan
                                  ID: {selectedPayment.planId}
                                </p>
                                {selectedPayment.productType && (
                                  <p className="text-xs text-gray-600">
                                    Categories:{" "}
                                    {Array.isArray(selectedPayment.productType)
                                      ? selectedPayment.productType.join(", ")
                                      : selectedPayment.productType}
                                  </p>
                                )}
                                {selectedPayment.renewDate && (
                                  <p className="flex gap-1 text-xs text-gray-800">
                                    Period: Until{" "}
                                    <div className="font-bold">
                                      {normalizeDate(
                                        selectedPayment.renewDate
                                      ).toLocaleDateString()}
                                    </div>
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="p-2 text-right">
                              <p className="text-lg font-bold text-gray-900">
                                {selectedPayment.currency === "USD" ? "$" : "₹"}
                                {((Number(selectedPayment.amount) || 0) / 100).toFixed(2)}
                              </p>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div className="flex justify-end border-t pt-2 mb-4">
                      <div className="text-right">
                        <p className="text-sm font-semibold">
                          Subtotal:{" "}
                          {selectedPayment.currency === "USD" ? "$" : "₹"}
                          {((Number(selectedPayment.amount) || 0) / 100).toFixed(2)}
                        </p>
                        <p className="text-xl font-bold text-gray-900 mt-1">
                          Total:{" "}
                          {selectedPayment.currency === "USD" ? "$" : "₹"}
                          {((Number(selectedPayment.amount) || 0) / 100).toFixed(2)}
                        </p>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 pt-2 border-t">
                      <div>
                        <h4 className="font-bold mb-1 text-xs">
                          Payment Details
                        </h4>
                        <div className="space-y-1 text-xs">
                          <p>
                            <span className="font-semibold">Method:</span>{" "}
                            {selectedPayment.paymentMode || "N/A"}
                          </p>
                          <p>
                            <span className="font-semibold">Status:</span>{" "}
                            {selectedPayment.status ||
                              (selectedPayment.isVerified
                                ? "Paid"
                                : "Cancelled")}
                          </p>
                          {selectedPayment.subscriptionId && (
                            <p>
                              <span className="font-semibold">Sub ID:</span>{" "}
                              {selectedPayment.subscriptionId}
                            </p>
                          )}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-bold mb-1 text-xs">Notes</h4>
                        <p className="text-xs text-gray-600">
                          Thank you! Contact support@
                          {institutionName.toLowerCase()}.com.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </main>
  );
};

export default Billing;